import { useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import { Typography, makeStyles } from '@material-ui/core';
import { isEmpty, get } from 'lodash';

import usePanelDataService from '../../../services/PanelDataService';
import { agentFilterId, dateTimeFilterId, testFilterId, timeRangeFilterId, useTest } from '../../filters';
import useStyles from '../../page-framework/panel/PanelStyles';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { getGraphData, getGraphOptions } from './PathVisualizationPanelUtilities';
import Panel from '../../page-framework/panel/PanelDecorator';
import { PathDetailVisualizationSection } from './PathDetailVisualizationSection';
import { useFilterState } from '../../../stores/FilterStateStore';

const useLocalStyles = makeStyles((theme) => ({
    infoMessage: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.alignItemsCenter,
        ...theme.mixins.flex.justifyContentCenter,

        '& svg': {
            marginRight: theme.spacing(0.5),
        },

        '& .MuiTypography-caption': {
            lineHeight: 'inherit',
        },

        '& a': {
            ...theme.mixins.flex.displayFlex,
        },
    },
    summaryContainer: {
        marginTop: '8px',
        width: '100%',
        justifyContent: 'center',
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.flexWrapNowrap,

        '& .MuiTypography-caption': {
            fontSize: 12,
        },
    },
}));

const dataSource = {
    url: '/ngp/v2/service-tests/path',
    params: {
        properties: 'path,graph',
        rowLimit: 1,
    },
};

function PathVisualizationPanel() {
    const classes = useLocalStyles();
    const panelClasses = useStyles();
    const test = useTest();
    const data = usePanelDataService(dataSource);
    const [filterState] = useFilterState();
    const [summary, graphData, graphOptions] = useMemo(() => {
        const pathData = data?.data?.[0];
        return [pathData?.summary, getGraphData(pathData), getGraphOptions(test, pathData)];
    }, [test, data]);
    const traceRouteProtocol = summary?.route?.protocol || 'TCP';

    if (!data) return null;

    return (
        <div className="flex-container">
            {!isEmpty(summary) && (
                <div className={classes.summaryContainer}>
                    <div className={panelClasses.panelStateSummaryItem}>
                        <Typography className={panelClasses.panelStateSummaryItemTitle} variant="caption">
                            <Trans>Time</Trans>:{' '}
                        </Typography>
                        <Typography className={panelClasses.panelStateSummaryItemValue} variant="caption">
                            {get(filterState, 'summarized.DateTime.value')}
                        </Typography>
                    </div>
                    <div className={panelClasses.panelStateSummaryItem}>
                        <Typography className={panelClasses.panelStateSummaryItemTitle} variant="caption">
                            <Trans>Protocol</Trans>:{' '}
                        </Typography>
                        <Typography className={panelClasses.panelStateSummaryItemValue} variant="caption">
                            {traceRouteProtocol}
                        </Typography>
                    </div>
                    <div className={panelClasses.panelStateSummaryItem}>
                        <Typography className={panelClasses.panelStateSummaryItemTitle} variant="caption">
                            <Trans>Target</Trans>:{' '}
                        </Typography>
                        <Typography className={panelClasses.panelStateSummaryItemValue} variant="caption">
                            {summary?.destination?.target}
                        </Typography>
                    </div>
                </div>
            )}
            <PathDetailVisualizationSection
                pathData={data.data[0]}
                testData={test}
                graphData={graphData}
                graphOptions={graphOptions}
            />
        </div>
    );
}

PathVisualizationPanel.propTypes = panelPropTypes;

PathVisualizationPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Path Visualization`,
    stateSummaryOptions: {
        show: false,
        excludedFilters: [timeRangeFilterId, testFilterId, agentFilterId, dateTimeFilterId],
    },
    autoHeight: true,
};

export default Panel({
    filters: {
        required: [timeRangeFilterId, dateTimeFilterId, testFilterId, agentFilterId],
        optional: [],
    },
})(PathVisualizationPanel);
